import { Source09fEnum, VideoAssetUsage } from '@goldcast/api/backend';
import { ClipAssetResponseSimple, ContentUpload } from '@goldcast/api/content';

import { VideoAsset } from './videoLibraryService';

import store from '@/store';
import { getEnv } from '@/utils/env';

export const STATIC_ASSETS = {
  ORG_PREFIX: 'organization',
  VIDEOS_PATH: 'videos'
};

export const ENTITY_TITLE_MAP = {
  EVENT: {
    Recording: 'Recording',
    Conference: 'Event',
    Webinar: 'Webinar'
  },
  HUB: 'Page',
  STUDIO_EVENT_TEMPLATE: 'Event Template'
} as const;

export const LINK_MAP = {
  EVENT: {
    Recording: '/recording-event',
    Conference: '/events',
    Webinar: '/webinar'
  },
  HUB: '/pages',
  STUDIO_EVENT_TEMPLATE: '/templates'
} as const;

export function GET_VIDEO_PATH(orgId) {
  return `${STATIC_ASSETS.ORG_PREFIX}/${orgId}/${STATIC_ASSETS.VIDEOS_PATH}/`;
}

export const APP_ENVIRONMENT = getEnv('VUE_APP_ENVIRONMENT');

export const processErrorParent = error => {
  try {
    JSON.parse(error.toString());
    return processError(error);
  } catch {
    return error;
  }
};

export const processError = error => {
  let message = 'Unknown Error Occurred !';
  try {
    const errors = JSON.parse(error.toString());
    if (Array.isArray(errors)) {
      if (errors.length > 0) {
        message = errors[0].message;
      }
    } else {
      const errorFields = Object.keys(errors);
      if (errorFields?.length > 0) {
        const error = errors[errorFields[0]];
        if (typeof error === 'string') {
          message = errors['message'] || errors['response'];
        } else if (typeof error === 'object') {
          message = error[0].message;
        }
      }
    }
  } catch (e) {
    return message;
  }
  if (!message || message.length === 0) {
    message = 'Unknown Error Occurred !';
  }
  return message;
};

export const redirectToUsage = (usage: VideoAssetUsage) => {
  const basePath =
    usage.entity_type === 'EVENT'
      ? LINK_MAP.EVENT[usage.entity_subtype as keyof typeof LINK_MAP.EVENT]
      : LINK_MAP[usage.entity_type as keyof typeof LINK_MAP];

  if (!basePath) {
    return;
  }

  const url = `${basePath}/${usage.entity_id}`;
  window.open(url, '_blank');
};

export const transformVideoAssetUsageEntityType = (usage: VideoAssetUsage): string => {
  if (usage.entity_type === 'EVENT') {
    return ENTITY_TITLE_MAP.EVENT[usage.entity_subtype as keyof typeof ENTITY_TITLE_MAP.EVENT] || usage.entity_type;
  }

  return ENTITY_TITLE_MAP[usage.entity_type] || usage.entity_type;
};

export const transformEditUrlForContentUpload = (videoAsset: VideoAsset, contentUpload: ContentUpload): string => {
  if (videoAsset.source !== 'CL_UPLOAD') {
    throw new Error('Video source is not a Content Lab Upload');
  }

  if (videoAsset.status === 'PROGRESSING') {
    return `/content/import/${contentUpload.project_id}/${contentUpload.id}/`;
  }

  return `/content/${contentUpload.project_id}/${contentUpload.id}/recordings`;
};

export const transformEditUrlForClipAsset = (videoAsset: VideoAsset, clipAsset: ClipAssetResponseSimple): string => {
  if (videoAsset.source === 'UPLOAD') {
    throw new Error('Manual uploads are not supported here.');
  }

  const { project, id: contentId } = clipAsset?.content || {};

  if (videoAsset.source === 'CLIP') {
    if (clipAsset?.saved_search) {
      return `/content/search/${clipAsset.saved_search.id}/clips?activeClipId=${clipAsset.id}`;
    }

    return `/content/${project?.id}/${contentId}/clips?activeClipId=${clipAsset?.id}`;
  }

  // Full length clip
  return `/content/${project?.id}/${contentId}/recordings`;
};

export function transformVideoAssetSource(source: Source09fEnum): string {
  const isUseCLUploadsInVLEnabled = store.state.org.currentOrg.Use_CL_Uploads_In_VL;

  if (source === 'DIRECT_RECORDING') {
    return 'Recording Studio';
  }

  if (source === 'EVENT_RECORDING') {
    return 'Event Recording';
  }

  if (source === 'UPLOAD') {
    return isUseCLUploadsInVLEnabled ? 'Event Asset' : 'Manual Upload';
  }

  if (source === 'CLIP') {
    return isUseCLUploadsInVLEnabled ? 'Clip' : 'Content Lab Clip';
  }

  if (source === 'CL_UPLOAD') {
    return isUseCLUploadsInVLEnabled ? 'Upload' : 'Content Lab Upload';
  }

  return '';
}

export type VideoUploadContext = 'video-library-page' | 'registration-page' | 'content-lab' | 'hub-page' | '';
